/**
 * Task #611 — Mobile-first /tour-day page.
 *
 * Shown to artist-role users on the road. Picks the next committed
 * tour stop for the artist's talent (today's stop if any, otherwise
 * the soonest upcoming one) and renders:
 *
 *   - "Today" block: city + state + drive time in.
 *   - One-tap directions button (geo: link, falls back to Google Maps web).
 *   - Hotel / transport / venue notes from the tour stop.
 *   - Next stop preview (drive miles + minutes).
 *
 * Money is NEVER displayed here — even if the per-talent flag is on.
 * The point of /tour-day is logistics for the artist on tour day.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, MapPin, Clock, Navigation, Hotel, Car, Music2 } from "lucide-react";
import type { Talent } from "@shared/schema";

interface Tour {
  id: string;
  talentId: string;
  name: string;
  status: string;
  totalMiles: number | null;
  totalDriveMinutes: number | null;
}
interface TourStop {
  id: string;
  city: string;
  state: string;
  sequence: number;
  proposedDate: string | null;
  hotelNotes: string | null;
  transportNotes: string | null;
  venueNotes: string | null;
  lat: number | null;
  lng: number | null;
  driveMilesFromPrev: number | null;
  driveMinutesFromPrev: number | null;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth() && a.getUTCDate() === b.getUTCDate();
}

function formatDriveTime(minutes: number | null | undefined): string {
  if (minutes == null) return "—";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatLongDate(iso: string | null): string {
  if (!iso) return "TBD";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

export default function TourDayPage() {
  // Resolve "my talent". Server scopes /api/talent to the caller's owned
  // talent automatically when role=artist.
  const talentQuery = useQuery<Talent[]>({ queryKey: ["/api/talent"] });
  const myTalent = talentQuery.data?.[0];

  const toursQuery = useQuery<Tour[]>({
    queryKey: ["/api/tours/by-talent", myTalent?.id],
    enabled: !!myTalent?.id,
    queryFn: async () => {
      const res = await fetch(`/api/tours/by-talent/${myTalent!.id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load tours");
      return res.json();
    },
  });

  // Pick the one tour that is "active" right now: status=committed and
  // contains a stop within the next 14 days. We then load that tour's stops.
  const activeTour = useMemo(() => {
    const tours = toursQuery.data ?? [];
    return tours.find((t) => t.status === "committed") ?? tours[0] ?? null;
  }, [toursQuery.data]);

  const detailQuery = useQuery<{ tour: Tour; stops: TourStop[] }>({
    queryKey: ["/api/tours", activeTour?.id, "detail"],
    enabled: !!activeTour?.id,
    queryFn: async () => {
      const res = await fetch(`/api/tours/${activeTour!.id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load tour");
      return res.json();
    },
  });

  const { todayStop, nextStop } = useMemo(() => {
    const stops = detailQuery.data?.stops ?? [];
    const now = new Date();
    let today: TourStop | null = null;
    let next: TourStop | null = null;
    for (const s of stops) {
      if (!s.proposedDate) continue;
      const d = new Date(s.proposedDate);
      if (isSameDay(d, now)) { today = s; }
      else if (d > now) { if (!next || new Date(next.proposedDate!) > d) next = s; }
    }
    // If nothing today, treat the next upcoming as "today" so the page
    // always has a focal stop. The next-block then shows the one after it.
    if (!today && next) {
      today = next;
      const idx = stops.findIndex((s) => s.id === next!.id);
      next = stops.slice(idx + 1).find((s) => s.proposedDate) ?? null;
    }
    return { todayStop: today, nextStop: next };
  }, [detailQuery.data]);

  if (talentQuery.isLoading || toursQuery.isLoading || detailQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!myTalent) {
    return (
      <div className="p-6 text-center text-muted-foreground" data-testid="text-tour-day-no-talent">
        Your account isn't linked to a talent profile yet.
      </div>
    );
  }

  if (!activeTour || !todayStop) {
    return (
      <div className="p-6 max-w-md mx-auto" data-testid="container-tour-day-empty">
        <h1 className="text-xl font-semibold mb-2">Tour Day</h1>
        <p className="text-muted-foreground">No active tour right now. Enjoy the day off.</p>
      </div>
    );
  }

  const directionsHref = todayStop.lat != null && todayStop.lng != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${todayStop.lat},${todayStop.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${todayStop.city}, ${todayStop.state}`)}`;

  return (
    <div className="min-h-screen bg-background" data-testid="container-tour-day">
      <div className="max-w-md mx-auto p-4 space-y-4">
        <div>
          <Badge variant="secondary" data-testid="badge-tour-name">{activeTour.name}</Badge>
          <h1 className="text-2xl font-bold mt-1">Tour Day</h1>
          <p className="text-sm text-muted-foreground">{formatLongDate(todayStop.proposedDate)}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span data-testid="text-today-city">{todayStop.city}, {todayStop.state}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {todayStop.driveMilesFromPrev != null && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span data-testid="text-today-drive">
                  {todayStop.driveMilesFromPrev} mi · {formatDriveTime(todayStop.driveMinutesFromPrev)} drive in
                </span>
              </div>
            )}
            <Button asChild className="w-full" data-testid="button-directions">
              <a href={directionsHref} target="_blank" rel="noopener noreferrer">
                <Navigation className="w-4 h-4 mr-2" /> Open Directions
              </a>
            </Button>
            {todayStop.hotelNotes && (
              <div className="flex items-start gap-2 text-sm">
                <Hotel className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div data-testid="text-today-hotel"><strong>Hotel:</strong> {todayStop.hotelNotes}</div>
              </div>
            )}
            {todayStop.transportNotes && (
              <div className="flex items-start gap-2 text-sm">
                <Car className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div data-testid="text-today-transport"><strong>Transport:</strong> {todayStop.transportNotes}</div>
              </div>
            )}
            {todayStop.venueNotes && (
              <div className="flex items-start gap-2 text-sm">
                <Music2 className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div data-testid="text-today-venue"><strong>Venue:</strong> {todayStop.venueNotes}</div>
              </div>
            )}
          </CardContent>
        </Card>

        {nextStop && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-muted-foreground">Next Up</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-semibold" data-testid="text-next-city">
                {nextStop.city}, {nextStop.state}
              </div>
              <div className="text-sm text-muted-foreground" data-testid="text-next-date">
                {formatLongDate(nextStop.proposedDate)}
                {nextStop.driveMilesFromPrev != null && (
                  <> · {nextStop.driveMilesFromPrev} mi · {formatDriveTime(nextStop.driveMinutesFromPrev)}</>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
